import { unwrap } from "@/lib/api";
import { api } from "./client";

/*
 * `POST /auth/logout` — die Sitzung beenden.
 *
 * ═══ Warum POST und kein `<a href>` ══════════════════════════════════════
 *
 * Der Server registriert den Endpunkt ausdrücklich als POST, und die
 * Begründung steht in seiner Spec-Beschreibung: „damit ein fremder Link
 * niemanden abmelden kann". Ein `<a href="/auth/logout">` wäre ein GET und
 * liefe ins Leere; ein Bild oder ein Link in einer Mail könnte sonst jeden
 * Betrachter abmelden. Deshalb ist dies das einzige Stück des Anmeldewegs,
 * das über den TYPISIERTEN CLIENT geht statt über eine Voll-Navigation.
 *
 * ═══ Warum der Pfad hier als Literal steht und nicht in `lib/auth-pfade.ts` ══
 *
 * `PFAD_ANMELDEN` gibt es dort, weil `/auth/login` in einem `href` steht —
 * eine Zeichenkette, die niemand prüft. Hier ist die Lage umgekehrt: der Pfad
 * ist ein Schlüssel aus `paths` in `schema.d.ts`, und der Compiler hält ihn
 * gegen die Spec. Gemessen (Wegwerf-Datei, danach entfernt): `api.POST(…)` mit
 * einem Pfad, den die Spec nicht kennt, bricht `pnpm typecheck` — und zwar
 * gleichermaßen, ob der Pfad als Literal oder aus einer Konstanten kommt.
 *
 * Eine Konstante brächte also keine zusätzliche Zusage, kostete aber eine:
 * `lib/stoerungstexte.test.ts` liest die Endpunkte eines api-Moduls per
 * `api.GET("…")`-Textscan. Hinter einer Konstanten wäre dieser Aufruf für ihn
 * unsichtbar — dieselbe Klasse wie der Umzug, der im achten Durchgang einem
 * Wächter seine Quelle entzogen hat. Alle anderen api-Module dieses
 * Verzeichnisses schreiben den Pfad ebenso literal.
 *
 * Ungeprüft ist der Pfad damit nicht: `lib/server-pfade.test.ts` leitet ihn
 * aus dem Spec-Tag `Auth` ab und stellt sicher, dass der Dev-Proxy ihn an Go
 * durchreicht — die Falle, die `/auth` fünf Durchgänge lang gestellt hat.
 */

/**
 * Beendet die Sitzung serverseitig und löscht das Cookie.
 *
 * ═══ Was der Server hier zusagt, am Draht gemessen ═══════════════════════
 *
 * ```
 * HTTP/1.1 204 No Content
 * Cache-Control: no-store
 * Set-Cookie: altware_session=; Path=/; Max-Age=0; HttpOnly; SameSite=Lax
 * ```
 *
 * **Immer 204**, auch ohne bestehende Sitzung und auch dann, wenn das Löschen
 * in der Datenbank scheitert (`handleAuthLogout` in `internal/api/auth.go`
 * begründet es: das Cookie ist danach so oder so weg, eine 500 ließe den
 * Nutzer mit gültigem Cookie und dem Eindruck stehen, er sei abgemeldet).
 *
 * Die Spec deklariert deshalb **keinen einzigen Fehler-STATUSCODE** — nur die
 * 204 und ein `default` mit `ErrorModel`, das Huma von sich aus anhängt (nur
 * zwei Operationen des ganzen Vertrags haben eines: diese und `GET
 * /api/health`; wer einen Code ausdrücklich deklariert, verliert es. Das ist
 * die genaue Mechanik hinter Punkt 24 der offenen Liste).
 *
 * Was schiefgehen kann, liegt also vor dem Handler: Netzausfall (`ApiError`
 * mit Status 0) oder ein Proxy, der eine 5xx erfindet. Genau darum bekommt der
 * Aufrufer hier **keine `StatusTexte`-Tabelle** mit: ein Satz zu einem Code,
 * den dieser Endpunkt nie sendet, wäre wirkungslos, und
 * `lib/stoerungstexte.test.ts` würde ihn melden — dessen Codemenge zählt das
 * `default` nicht mit, weil `Number("default")` kein Code ist.
 *
 * Das Cookie ist `HttpOnly` — die SPA kann es weder lesen noch löschen. Der
 * einzige Weg hinaus führt über diesen Aufruf; scheitert er, ist der Nutzer
 * noch angemeldet, und das muss die Sicht auch sagen.
 */
export async function meldeAb(): Promise<void> {
  // Kein `signal`: ein abgebrochenes Abmelden ist der schlechteste Ausgang —
  // der Nutzer hält sich für abgemeldet, das Cookie lebt weiter.
  unwrap(await api.POST("/auth/logout"));
}
